import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { formatDate, formatCurrency } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Clock,
  Plus,
  ChevronLeft,
  ChevronRight,
  Loader2,
  DollarSign,
  Timer,
  CheckCircle,
} from 'lucide-react';

interface TimeEntry {
  id: string;
  projectId: string;
  projectName: string;
  taskId: string | null;
  taskName: string | null;
  userId: string;
  userName: string;
  date: string;
  hours: number;
  description: string | null;
  isBillable: boolean;
  billingRate: number | null;
  totalAmount: number | null;
  isInvoiced: boolean;
}

interface Project {
  id: string;
  name: string;
  hourlyRate: number | null;
}

interface Task {
  id: string;
  name: string;
  projectId: string;
}

interface TimeEntrySummary {
  totalHours: number;
  billableHours: number;
  totalBillableAmount: number;
}

export function TimeEntriesPage() {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const initialProject = searchParams.get('project') || '';
  const [projectFilter, setProjectFilter] = useState(initialProject);
  const [billableFilter, setBillableFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<TimeEntry | null>(null);
  const [selectedProjectId, setSelectedProjectId] = useState('');

  // Fetch active projects for filter and dialog dropdowns
  const { data: projectsData } = useQuery({
    queryKey: ['projects', 'active'],
    queryFn: () => api.listProjects({ status: 'active', limit: 100 }),
  });

  // Fetch tasks filtered by selected project (for dialog)
  const { data: tasksData } = useQuery({
    queryKey: ['projectTasks', selectedProjectId],
    queryFn: () => api.listProjectTasks({ projectId: selectedProjectId, limit: 100 }),
    enabled: !!selectedProjectId,
  });

  // Fetch time entries with filters
  const { data, isLoading } = useQuery({
    queryKey: ['timeEntries', { page, projectId: projectFilter, dateFrom, dateTo, isBillable: billableFilter }],
    queryFn: () => api.listTimeEntries({
      page,
      limit: 20,
      projectId: projectFilter || undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      isBillable: billableFilter === '' ? undefined : billableFilter === 'yes',
    }),
  });

  const projects = (projectsData?.data || []) as Project[];
  const tasks = (tasksData?.data || []) as Task[];
  const entries = (data?.data || []) as TimeEntry[];
  const summary = ((data as unknown as Record<string, unknown>)?.summary || {}) as TimeEntrySummary;
  const pagination = data?.pagination;

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => api.createTimeEntry(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timeEntries'] });
      setDialogOpen(false);
      setSelectedProjectId('');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) => api.updateTimeEntry(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timeEntries'] });
      setDialogOpen(false);
      setEditingEntry(null);
      setSelectedProjectId('');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteTimeEntry(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timeEntries'] });
    },
  });

  function openCreate() {
    setEditingEntry(null);
    setSelectedProjectId('');
    setDialogOpen(true);
  }

  function openEdit(entry: TimeEntry) {
    setEditingEntry(entry);
    setSelectedProjectId(entry.projectId);
    setDialogOpen(true);
  }

  function handleProjectChangeInDialog(projectId: string) {
    setSelectedProjectId(projectId);
  }

  function getProjectHourlyRate(projectId: string): number | null {
    const project = projects.find(p => p.id === projectId);
    return project?.hourlyRate ?? null;
  }

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const projectId = formData.get('projectId') as string;
    const payload: Record<string, unknown> = {
      projectId,
      taskId: formData.get('taskId') || null,
      date: formData.get('date'),
      hours: parseFloat(formData.get('hours') as string),
      description: formData.get('description') || null,
      isBillable: formData.get('isBillable') === 'on',
      billingRate: formData.get('billingRate') ? parseFloat(formData.get('billingRate') as string) : null,
    };

    if (editingEntry) {
      updateMutation.mutate({ id: editingEntry.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Time Entries</h1>
          <p className="text-muted-foreground">Track time spent on projects and tasks</p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="h-4 w-4 mr-2" />
          Log Time
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10"><Timer className="h-5 w-5 text-primary" /></div>
              <div>
                <p className="text-sm text-muted-foreground">Total Hours</p>
                <p className="text-xl font-bold">{(summary.totalHours || 0).toFixed(1)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10"><CheckCircle className="h-5 w-5 text-blue-500" /></div>
              <div>
                <p className="text-sm text-muted-foreground">Billable Hours</p>
                <p className="text-xl font-bold">{(summary.billableHours || 0).toFixed(1)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-500/10"><DollarSign className="h-5 w-5 text-green-500" /></div>
              <div>
                <p className="text-sm text-muted-foreground">Total Billable Amount</p>
                <p className="text-xl font-bold">{formatCurrency(summary.totalBillableAmount || 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <Select value={projectFilter} onValueChange={(v) => { setProjectFilter(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="All projects" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All projects</SelectItem>
            {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input
          type="date"
          className="w-40"
          value={dateFrom}
          onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
          placeholder="From date"
        />
        <Input
          type="date"
          className="w-40"
          value={dateTo}
          onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
          placeholder="To date"
        />
        <Select value={billableFilter} onValueChange={(v) => { setBillableFilter(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-36">
            <SelectValue placeholder="All entries" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All entries</SelectItem>
            <SelectItem value="yes">Billable</SelectItem>
            <SelectItem value="no">Non-billable</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : entries.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Clock className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No time entries found</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left p-3 text-sm font-medium">Date</th>
                  <th className="text-left p-3 text-sm font-medium">Project</th>
                  <th className="text-left p-3 text-sm font-medium">Task</th>
                  <th className="text-right p-3 text-sm font-medium">Hours</th>
                  <th className="text-left p-3 text-sm font-medium">Description</th>
                  <th className="text-center p-3 text-sm font-medium">Billable</th>
                  <th className="text-right p-3 text-sm font-medium">Rate</th>
                  <th className="text-right p-3 text-sm font-medium">Amount</th>
                  <th className="text-right p-3 text-sm font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <tr key={entry.id} className="border-b hover:bg-muted/30">
                    <td className="p-3 text-sm">{formatDate(entry.date)}</td>
                    <td className="p-3 text-sm font-medium">{entry.projectName}</td>
                    <td className="p-3 text-sm">{entry.taskName || '—'}</td>
                    <td className="p-3 text-sm text-right font-medium">{entry.hours.toFixed(1)}</td>
                    <td className="p-3 text-sm max-w-[200px] truncate">{entry.description || '—'}</td>
                    <td className="p-3 text-center">
                      {entry.isBillable ? (
                        <Badge variant="secondary">Billable</Badge>
                      ) : (
                        <Badge variant="outline">Non-billable</Badge>
                      )}
                    </td>
                    <td className="p-3 text-sm text-right">
                      {entry.billingRate != null ? formatCurrency(entry.billingRate) : '—'}
                    </td>
                    <td className="p-3 text-sm text-right font-medium">
                      {entry.totalAmount != null ? formatCurrency(entry.totalAmount) : '—'}
                    </td>
                    <td className="p-3 text-right">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(entry)}>Edit</Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive"
                        onClick={() => { if (confirm('Delete this time entry?')) deleteMutation.mutate(entry.id); }}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {pagination.total} entr{pagination.total !== 1 ? 'ies' : 'y'}
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm">Page {page} of {pagination.totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= pagination.totalPages} onClick={() => setPage(p => p + 1)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Log Time / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) { setEditingEntry(null); setSelectedProjectId(''); } }}>
        <DialogContent key={editingEntry?.id || 'new'} className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingEntry ? 'Edit Time Entry' : 'Log Time'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="projectId">Project *</Label>
                <select
                  name="projectId"
                  id="projectId"
                  required
                  defaultValue={editingEntry?.projectId || ''}
                  onChange={(e) => handleProjectChangeInDialog(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="">Select project</option>
                  {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <Label htmlFor="taskId">Task</Label>
                <select
                  name="taskId"
                  id="taskId"
                  defaultValue={editingEntry?.taskId || ''}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="">No task</option>
                  {tasks.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="date">Date *</Label>
                <Input
                  type="date"
                  name="date"
                  id="date"
                  required
                  defaultValue={editingEntry?.date ? editingEntry.date.split('T')[0] : new Date().toISOString().split('T')[0]}
                />
              </div>
              <div>
                <Label htmlFor="hours">Hours *</Label>
                <Input
                  type="number"
                  name="hours"
                  id="hours"
                  step="0.25"
                  min="0.25"
                  required
                  defaultValue={editingEntry?.hours || ''}
                />
              </div>
            </div>
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea name="description" id="description" rows={3} defaultValue={editingEntry?.description || ''} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-2 pt-6">
                <input
                  type="checkbox"
                  name="isBillable"
                  id="isBillable"
                  defaultChecked={editingEntry ? editingEntry.isBillable : true}
                  className="h-4 w-4"
                />
                <Label htmlFor="isBillable">Billable</Label>
              </div>
              <div>
                <Label htmlFor="billingRate">Billing Rate</Label>
                <Input
                  type="number"
                  name="billingRate"
                  id="billingRate"
                  step="0.01"
                  min="0"
                  defaultValue={
                    editingEntry?.billingRate ??
                    (selectedProjectId ? getProjectHourlyRate(selectedProjectId) ?? '' : '')
                  }
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                {(createMutation.isPending || updateMutation.isPending) && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingEntry ? 'Update' : 'Log Time'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
